import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { IS_MOBILE } from '@blocksuite/global/env';
import { CloseIcon, SearchIcon } from '@blocksuite/icons/lit';
import { effect } from '@preact/signals-core';
import { baseTheme } from '@streamlain/theme';
import { css, html, unsafeCSS } from 'lit';
import { query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import debounce from 'lodash-es/debounce';

import { dataViewI18nT } from '../../../../core/i18n.js';
import {
  DataViewSearchCollaboration,
  type DataViewSearchUiState,
} from '../../../../core/search-collaboration.js';
import { stopPropagation } from '../../../../core/utils/event.js';
import { WidgetBase } from '../../../../core/widget/widget-base.js';
import type { KanbanViewUILogic } from '../../../../view-presets/kanban/pc/kanban-view-ui-logic.js';
import type { VirtualTableViewUILogic } from '../../../../view-presets/table/pc-virtual/table-view-ui-logic.js';

// Large sharded tables can make a full search projection expensive. Keep the
// trailing window comfortably above ordinary typing cadence so one deliberate
// query produces one projection instead of starting work during a short pause.
const SEARCH_INPUT_DEBOUNCE_MS = 600;

const styles = css`
  .affine-database-search-container {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: width 0.3s ease;
    overflow: hidden;
  }

  .search-container-expand {
    overflow: visible;
    width: 160px;
    background-color: var(--streamlain-hover-color);
  }

  .search-input-container {
    display: flex;
    align-items: center;
  }

  .close-icon {
    display: flex;
    align-items: center;
    padding-right: 8px;
    height: 100%;
    cursor: pointer;
  }

  .affine-database-search-input-icon {
    position: absolute;
    left: 0;
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    color: ${unsafeCSSVarV2('icon/primary')};
  }

  .affine-database-search-input-icon:hover {
    background: var(--streamlain-hover-color);
  }

  .search-container-expand .affine-database-search-input-icon {
    left: 4px;
    pointer-events: none;
  }

  .affine-database-search-input {
    flex: 1;
    width: 100%;
    padding: 0 2px 0 30px;
    border: none;
    font-family: ${unsafeCSS(baseTheme.fontSansFamily)};
    font-size: var(--streamlain-font-sm);
    box-sizing: border-box;
    color: inherit;
    background: transparent;
    outline: none;
  }

  .affine-database-search-input::placeholder {
    color: var(--streamlain-placeholder-color);
    font-size: var(--streamlain-font-sm);
  }
`;

export class DataViewHeaderToolsSearch extends WidgetBase<
  VirtualTableViewUILogic | KanbanViewUILogic
> {
  static override styles = styles;

  private readonly _commitSearch = debounce((search: string) => {
    this._setSearchState({ expanded: true, input: search, query: search });
  }, SEARCH_INPUT_DEBOUNCE_MS);

  private readonly _clearSearch = () => {
    this._commitSearch.cancel();
    this._searchInput.value = '';
    this._setSearchState({ expanded: true, input: '', query: '' });
    this.preventBlur = true;
    setTimeout(() => {
      this.preventBlur = false;
    });
  };

  private readonly _clickSearch = (e: MouseEvent) => {
    e.stopPropagation();
    this._setSearchState({
      expanded: true,
      input: this.dataViewLogic.view.search$.value,
      query: this.dataViewLogic.view.search$.value,
    });
    this.updateComplete.then(() => this._searchInput?.focus()).catch(() => {});
  };

  private readonly _onSearch = (event: InputEvent) => {
    event.stopPropagation();
    const el = event.target as HTMLInputElement;
    this._publishSearchInput(el.value);
    this._commitSearch(el.value.trim());
  };

  private readonly _onSearchBlur = () => {
    this._commitSearch.flush();
    if (this._searchInput.value || this.preventBlur) {
      return;
    }
    this._setSearchState({ expanded: false, query: '' });
  };

  private readonly _onSearchKeydown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      this._commitSearch.flush();
      return;
    }
    if (event.key === 'Escape') {
      this._commitSearch.cancel();
      if (this._searchInput.value) {
        this._searchInput.value = '';
        this._setSearchState({ expanded: true, input: '', query: '' });
      } else {
        this._setSearchState({ expanded: false, input: '', query: '' });
      }
    }
  };

  private _searchCollaboration() {
    return this.dataViewLogic.view.serviceGet(DataViewSearchCollaboration);
  }

  private _setSearchState(state: DataViewSearchUiState) {
    const collaboration = this._searchCollaboration();
    if (collaboration) {
      collaboration.update(this.dataViewLogic.view.id, state);
    }
    this.dataViewLogic.view.setSearch(state.query);
    this.showSearch = state.expanded;
  }

  private _publishSearchInput(input: string) {
    const collaboration = this._searchCollaboration();
    collaboration?.update(this.dataViewLogic.view.id, {
      expanded: true,
      input,
      query: this.dataViewLogic.view.search$.value,
    });
  }

  preventBlur = false;

  override connectedCallback() {
    super.connectedCallback();
    this.style.display = IS_MOBILE ? 'none' : 'flex';
    const collaboration = this._searchCollaboration();
    if (collaboration) {
      this.disposables.add(
        effect(() => {
          const state = collaboration.states$.value[this.dataViewLogic.view.id];
          if (!state) {
            return;
          }
          if (this.dataViewLogic.view.search$.value !== state.query) {
            this.dataViewLogic.view.setSearch(state.query);
          }
          this.showSearch = state.expanded;
          this.requestUpdate();
        })
      );
    }
  }

  override disconnectedCallback() {
    this._commitSearch.cancel();
    super.disconnectedCallback();
  }

  override render() {
    const collaborationState =
      this._searchCollaboration()?.states$.value[this.dataViewLogic.view.id];
    const searchValue =
      collaborationState?.input ?? this.dataViewLogic.view.search$.value;
    const showSearch = this.showSearch || !!searchValue;
    const searchToolClassMap = classMap({
      'affine-database-search-container': true,
      'search-container-expand': showSearch,
      active: showSearch,
    });
    return html`
      <label
        class="${searchToolClassMap}"
        title="Search"
        @click="${this._clickSearch}"
      >
        <div class="affine-database-search-input-icon">${SearchIcon()}</div>
        <input
          placeholder=${dataViewI18nT('toolbar.searchPlaceholder')}
          class="affine-database-search-input"
          .value="${searchValue}"
          @input="${this._onSearch}"
          @click="${(event: MouseEvent) => event.stopPropagation()}"
          @keydown="${this._onSearchKeydown}"
          @pointerdown="${stopPropagation}"
          @blur="${this._onSearchBlur}"
        />
        <div class="close-icon" @mousedown="${this._clearSearch}">
          ${CloseIcon()}
          <affine-tooltip>
            <span
              style=${styleMap({
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--streamlain-white-10)',
              })}
              >Esc</span
            >
            ${dataViewI18nT('toolbar.searchClear')}
          </affine-tooltip>
        </div>
      </label>
    `;
  }

  @query('.affine-database-search-input')
  private accessor _searchInput!: HTMLInputElement;

  @state()
  private accessor showSearch = false;
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-header-tools-search': DataViewHeaderToolsSearch;
  }
}
